import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiHeart, FiX, FiArrowRight } from 'react-icons/fi';
import { useShop } from '../../context/ShopContext';
import { useDiscount } from '../../context/DiscountContext';
import { cashPriceFromMrp, getProductDiscount, formatPrice } from '../../utils/cashPricing';
import './WishlistAnimationModal.css';

const WishlistAnimationModal = () => {
    const { activeWishlistModal, closeWishlistModal, wishlistCount } = useShop();
    const { effectiveDiscount } = useDiscount();
    const navigate = useNavigate();
    const [isClosing, setIsClosing] = useState(false);

    const handleManualClose = useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            closeWishlistModal();
            setIsClosing(false);
        }, 220);
    }, [closeWishlistModal]);

    useEffect(() => {
        if (!activeWishlistModal?.isOpen) {
            setIsClosing(false);
            return undefined;
        }

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                handleManualClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeWishlistModal, handleManualClose]);

    if (!activeWishlistModal?.isOpen) return null;

    const { product } = activeWishlistModal;
    const mrp = Number(product?.mrp || product?.price || 0);
    const discount = getProductDiscount(product, effectiveDiscount);
    const cashPrice = mrp > 0 ? cashPriceFromMrp(mrp, discount) : 0;

    const handleViewWishlist = () => {
        closeWishlistModal();
        navigate('/wishlist');
    };

    return (
        <div
            className={`wishlist-anim-overlay ${isClosing ? 'is-closing' : ''}`}
            onClick={handleManualClose}
            role="dialog"
            aria-modal="true"
            aria-label="Item added to wishlist confirmation"
        >
            <div
                className={`wishlist-anim-popup ${isClosing ? 'is-closing' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button */}
                <button
                    type="button"
                    className="wishlist-anim__close-btn"
                    onClick={handleManualClose}
                    aria-label="Close"
                    title="Close"
                >
                    <FiX />
                </button>

                {/* Animated 3D Heart Stage */}
                <div className="wishlist-heart-stage">
                    {/* Floating Heart Particles */}
                    <div className="heart-particles" aria-hidden="true">
                        <span className="h-part hp1">♥</span>
                        <span className="h-part hp2">★</span>
                        <span className="h-part hp3">♥</span>
                        <span className="h-part hp4">✦</span>
                        <span className="h-part hp5">♥</span>
                    </div>

                    {/* Central 3D Beating Heart */}
                    <div className="beating-3d-heart">
                        <svg
                            viewBox="0 0 100 100"
                            className="heart-svg"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <defs>
                                <linearGradient id="heartGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#f43f5e" />
                                    <stop offset="50%" stopColor="#e11d48" />
                                    <stop offset="100%" stopColor="#be123c" />
                                </linearGradient>
                                <radialGradient id="heartGlow" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor="rgba(244, 63, 94, 0.6)" />
                                    <stop offset="100%" stopColor="rgba(244, 63, 94, 0)" />
                                </radialGradient>
                                <filter id="heartShadow" x="-20%" y="-20%" width="140%" height="140%">
                                    <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#f43f5e" floodOpacity="0.6" />
                                </filter>
                            </defs>
                            <circle cx="50" cy="50" r="48" fill="url(#heartGlow)" />
                            <path
                                d="M 50 82 C 50 82 18 58 18 36 C 18 22 28 14 39 14 C 45 14 50 18 50 20 C 50 18 55 14 61 14 C 72 14 82 22 82 36 C 82 58 50 82 50 82 Z"
                                fill="url(#heartGrad)"
                                filter="url(#heartShadow)"
                            />
                            {/* Glossy Reflection Highlight */}
                            <ellipse
                                cx="36"
                                cy="28"
                                rx="9"
                                ry="5"
                                fill="rgba(255, 255, 255, 0.45)"
                                transform="rotate(-30 36 28)"
                            />
                        </svg>
                    </div>

                    {/* Badge */}
                    <div className="wishlist-badge-pop">
                        <FiHeart className="badge-heart-icon" />
                        <span>Saved to Favourites!</span>
                    </div>
                </div>

                {/* Product Card Details */}
                <div className="wishlist-popup__product-row">
                    <img
                        src={
                            product?.image ||
                            'https://res.cloudinary.com/fl3tts9v/image/upload/v1790983151/category-air-conditioners_xtx2ok.jpg'
                        }
                        alt={product?.name}
                        className="wishlist-popup__thumb"
                    />
                    <div className="wishlist-popup__details">
                        <span className="wishlist-popup__brand">
                            {product?.brand || 'Rahber Corporation'}
                        </span>
                        <h4 className="wishlist-popup__name">
                            {product?.name || 'Home Appliance'}
                        </h4>
                        <div className="wishlist-popup__price-row">
                            <span className="wishlist-popup__price">
                                {formatPrice(cashPrice || mrp)}
                            </span>
                            {discount > 0 && cashPrice > 0 && cashPrice < mrp ? (
                                <span className="wishlist-popup__tag">
                                    -{discount}% Cash Deal
                                </span>
                            ) : (
                                <span className="wishlist-popup__tag">
                                    In Wishlist
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="wishlist-popup__actions">
                    <button
                        type="button"
                        className="wishlist-popup__btn-continue"
                        onClick={handleManualClose}
                    >
                        Continue Browsing
                    </button>
                    <button
                        type="button"
                        className="wishlist-popup__btn-view"
                        onClick={handleViewWishlist}
                    >
                        View Favourites ({wishlistCount}) <FiArrowRight />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WishlistAnimationModal;
